import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Users, 
  BarChart3, 
  ArrowRight,
  Lock,
  Target,
  Shield
} from 'lucide-react';
import { usePageMeta } from '../lib/usePageMeta';

export default function Landing() {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Wricoo - Web Content Operations Platform',
    description: 'Make your SEOs and Writers work together. Wricoo is built for digital marketing agencies and in-house content teams.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/',
  });
  const features = [
    { icon: FileText, title: 'Structured SEO Briefs', description: 'Create comprehensive briefs with keywords, objectives, and guidelines in one place.' },
    { icon: Lock, title: 'Locked Headings', description: 'SEO controls the outline. Writers focus on content without disrupting structure.' },
    { icon: BarChart3, title: 'Smart Word Count', description: 'Track writer words separately from total. Real-time counting as you type.' },
    { icon: Users, title: 'Role-Based Access', description: 'SEO, Writer, Reviewer — each sees only what they need, when they need it.' },
    { icon: Target, title: 'Inline Comments', description: 'Leave feedback directly on content blocks. No more lost email threads.' },
    { icon: Shield, title: 'Content Security', description: 'Keep your content safe with permission controls and locked content blocks.' },
  ];

  const workflowSteps = [
    { step: '1', title: 'Create Brief', description: 'SEO team sets up keywords, guidelines, and structure.', color: 'bg-blue-500' },
    { step: '2', title: 'Assign Writer', description: 'Assign a writer and set the article to Ready to Write.', color: 'bg-indigo-500' },
    { step: '3', title: 'Write Content', description: 'Writer creates content within the locked structure.', color: 'bg-violet-500' },
    { step: '4', title: 'Review & Edit', description: 'Reviewer adds comments or approves the content.', color: 'bg-purple-500' },
    { step: '5', title: 'Finalize', description: 'Content is finalized and ready for publishing.', color: 'bg-emerald-500' },
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-xl font-bold text-white tracking-tight">Wricoo</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-blue-100 hover:text-white transition">Product</a>
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-blue-100 hover:text-white transition">Tools</Link>
            <Link to="/blog/" className="text-sm font-medium text-blue-100 hover:text-white transition">Blog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-bold text-gray-900 leading-tight mb-6">
            Make Your Web Content Operations Smooth
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            Make your SEOs and Writers work together. Get rid of Google Sheets and Google Documents for a better change. <strong className="text-gray-800">Wricoo</strong> is built for digital marketing agencies and in-house content teams.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" className="h-12 px-8" onClick={() => navigate('/register/')}>
              Start for Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button variant="outline" size="lg" className="h-12 px-8" onClick={() => {
              document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              See How It Works
            </Button>
          </div>
          <p className="mt-4 text-sm text-gray-500">Free plan includes 1 workspace and up to 5 team members.</p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-bold text-gray-900 mb-3">Built for Content Teams</h2>
            <p className="text-gray-600 max-w-xl mx-auto">Every feature designed to eliminate friction between SEO strategy and content execution.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-bold text-gray-900 mb-3">A Workflow That Makes Sense</h2>
            <p className="text-gray-600 max-w-xl mx-auto">From brief to publication, every step is tracked and every role knows what to do next.</p>
          </div>
          <div className="relative">
            <div className="hidden lg:flex items-start justify-between relative">
              <div className="absolute top-6 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-blue-300 via-violet-300 to-emerald-300" />
              {workflowSteps.map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center relative z-10 w-1/5 px-2">
                  <div className={`w-12 h-12 rounded-full ${item.color} text-white flex items-center justify-center text-lg font-bold shadow-lg mb-4`}>{item.step}</div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
            <div className="hidden sm:flex lg:hidden overflow-x-auto gap-6 pb-4 snap-x snap-mandatory">
              {workflowSteps.map((item, i) => (
                <div key={i} className="flex-shrink-0 w-48 snap-center bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                  <div className={`w-10 h-10 rounded-full ${item.color} text-white flex items-center justify-center text-sm font-bold mb-3`}>{item.step}</div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
              ))}
            </div>
            <div className="flex sm:hidden flex-col gap-3">
              {workflowSteps.map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className={`w-10 h-10 rounded-full ${item.color} text-white flex items-center justify-center text-sm font-bold flex-shrink-0`}>{item.step}</div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-bold text-gray-900 mb-3">Ready to Streamline Your Content Workflow?</h2>
          <p className="text-gray-600 max-w-xl mx-auto mb-8">Start free. No credit card required. See how Wricoo brings structure to content operations.</p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" className="h-12 px-8" onClick={() => navigate('/register/')}>
              Start for Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button variant="outline" size="lg" className="h-12 px-8" asChild>
              <Link to="/pricing/">View Pricing</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500" data-testid="footer-links">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
